import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import TFCard from '../../components/TFCard/TFCard';
import ReactGoogleMaps from '../../components/ReactGoogleMaps';
import { Airport } from '../../models/airports';
import { getRoutes } from '../../services/api';
import { addToast, ToastCategories, ToastTypes } from '../../models/toasts';

interface RoutesMapProps {
  aircraftId: string;
  currentAirport: Airport;
}

interface Route {
  arrival: {
    ident: string;
    latitude: string;
    longitude: string;
    name: string;
  };
  departure: {
    ident: string;
    latitude: string;
    longitude: string;
    name: string;
  };
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  @media (max-width: 451px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colours.black};
  @media (max-width: 451px) {
    margin-bottom: 8px;
  }
`;

const DateSelectionWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const ShowingTitle = styled.div`
  color: ${({ theme }) => theme.colours.black};
  font-weight: 500;
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TimeButton = styled.button<{ active: boolean }>`
  margin: 0;
  color: ${({ theme, active }) => active ? theme.colours.blue : theme.colours.black};
  font-variant: tabular-nums;
  list-style: none;
  font-feature-settings: 'tnum';
  display: inline-block;
  height: auto;
  margin-right: 8px;
  padding: 0 7px;
  font-size: 12px;
  line-height: 20px;
  white-space: nowrap;
  background: ${({ theme, active }) => active ? theme.colours.lightBlueLight : theme.colours.lightGrayExtraLight};
  border: ${({ theme, active }) =>
    active ? `1px solid ${theme.colours.lightBlue}` : `1px solid ${theme.colours.lightGrayMedium}`};
  border-radius: 4px;
  cursor: pointer;
  opacity: 1;
  transition: all 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86);
  font-weight: 500;
`;

const TIME_OPTIONS = [
  {
    type: 'week',
    timeRange: { from: moment().subtract(1, 'weeks').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') },
  },
  {
    type: 'month',
    timeRange: { from: moment().subtract(30, 'days').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') },
  },
  {
    type: '3month',
    timeRange: { from: moment().subtract(90, 'days').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') },
  },
  {
    type: 'all',
    timeRange: { from: moment('1900-01-01').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') },
  },
];

const RoutesMap: React.FC<RoutesMapProps> = ({ aircraftId, currentAirport }) => {
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('week');
  const [zoomVal, setZoomVal] = useState(1);
  const [maxZoomOut, setMaxZoomOut] = useState(2);
  const [routes, setRoutes] = useState<Route[]>([]);

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const getAircraftRoutes = useCallback((timeRange: { from: string; to: string }) => {
    if (aircraftId) {
      getRoutes({
        from: timeRange.from,
        to: timeRange.to,
        aircraft: aircraftId,
      }).then((data) => {
        setRoutes(data);
      }).catch(() => {
        setRoutes([]);
        dispatch(addToast({
          payload: {
            title: formatMessage({ id: 'text.errorGettingRoutes' }),
            message: formatMessage({ id: 'text.thereWasAnErrorSourcingRoutes' }),
            type: ToastTypes.ERROR,
            category: ToastCategories.FLAG,
          },
        }));
      });
    }
  }, [aircraftId, dispatch, formatMessage]);

  const handleTimePeriodChange = (timeOption: { type: string; timeRange: { from: string; to: string } }) => {
    setSelectedTimePeriod(timeOption.type);
    getAircraftRoutes(timeOption.timeRange);
  };

  const buildTimeButtons = () => {
    return TIME_OPTIONS.map((option) => {
      let buttonText = formatMessage({ id: 'period.last7Days' });

      if (option.type === 'month') buttonText = formatMessage({ id: 'period.last30Days' });
      if (option.type === '3month') buttonText = formatMessage({ id: 'period.last90Days' });
      if (option.type === 'all') buttonText = formatMessage({ id: 'period.allTime' });

      return (
        <TimeButton
          active={option.type === selectedTimePeriod}
          onClick={() => handleTimePeriodChange(option)}
          key={option.type}
        >
          {buttonText}
        </TimeButton>
      )
    })
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1800) {
        setMaxZoomOut(3);
        setZoomVal(2);
      } else if (window.innerWidth < 1800 && window.innerWidth > 1200) {
        setMaxZoomOut(2);
        setZoomVal(2);
      } else if (window.innerWidth <= 1200 && window.innerWidth > 992) {
        setMaxZoomOut(1);
        setZoomVal(2);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    getAircraftRoutes(TIME_OPTIONS[0].timeRange);

    return () => window.removeEventListener('resize', handleResize);
  }, [getAircraftRoutes]);

  return (
    <TFCard>
      <Header>
        <Title>{formatMessage({ id: 'title.routes' })}</Title>
        <DateSelectionWrapper>
          <ShowingTitle>{formatMessage({ id: 'text.showing' })}</ShowingTitle>
          <ButtonWrapper>
            {buildTimeButtons()}
          </ButtonWrapper>
        </DateSelectionWrapper>
      </Header>
      <ReactGoogleMaps
        routes={routes}
        initialZoom={zoomVal}
        maxZoomOut={maxZoomOut}
        currentLocation={currentAirport}
        mapSource="Overview"
        containerWidth="100%"
        containerHeight="350px"
        fullscreenControl={false}
        data-test="routesMap"
      />
    </TFCard>
  );
};

export default RoutesMap;
